import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { parse } from 'query-string';
import DefaultMsgPage from 'components/DefaultMsgPage';
import { useLocation } from 'react-router-dom';
import { SmlsAnimation } from '@smiles/smiles-ui-kit-react';
import { useTranslation } from 'react-i18next';
import { validateTokenEmail } from 'services/core/services';
import { loginDataLayer } from 'utils/dataLayer';

const Email: React.FC = () => {
  const [isLoadingAnimation, setIsLoadingAnimation] = useState({ isSuccess: true, isLoading: true });
  const [loadingFinishMode, setOnLoadingFinishMode] = useState<'yes' | 'wait' | 'no' | undefined>('wait');
  const [validToken, setValidToken] = useState<boolean>(false);
  const [isGenericError, setIsGenericError] = useState(false);
  const [onFinishLoading, setOnFinishLoading] = useState<() => void>();
  const { t } = useTranslation('validate_email');
  const location = useLocation();

  const handleGoHome = useCallback(async () => {
    window.location.href = `${window.location.origin}/logout`;
  }, []);

  const handleSendAgain = useCallback(async () => {
    window.location.href = `${window.location.origin}/login?resendEmail=true`;
  }, []);

  const params = useMemo(() => parse(location.search), [location]);

  const validateToken = async (tk: string) => {
    const resp = await validateTokenEmail(tk);

    if (!resp) {
      setIsGenericError(true);
      return false;
    }
    if (!resp.success) {
      const [error] = resp.errors.details;
      if (error && error.statusCode !== 452 && error.errorCode !== 3) {
        setIsGenericError(true);
      }
    }
    return resp.success;
  };

  useEffect(() => {
    const getToken = async () => {
      setIsLoadingAnimation(() => ({ isSuccess: true, isLoading: true }));
      setOnLoadingFinishMode('wait');

      const resp = await validateToken(params.token as string);
      setValidToken(resp as boolean);

      if (!params.token || !resp) {
        setOnLoadingFinishMode('no');
        setIsLoadingAnimation(() => ({ isSuccess: false, isLoading: false }));
        return;
      }

      setOnFinishLoading(() => {
        handleGoHome();
      });
      setIsLoadingAnimation(() => ({ isSuccess: true, isLoading: false }));
    };
    getToken();
  }, []);

  useEffect(() => {
    if (isLoadingAnimation.isSuccess === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-email/sucesso', 3);
    }
  }, [isLoadingAnimation]);

  useEffect(() => {
    if (!validToken && !isGenericError && !isLoadingAnimation.isLoading) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-email/link-expirado', 3);
    }
  }, [validToken, isGenericError, isLoadingAnimation]);

  useEffect(() => {
    if (isGenericError === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-email/erro-sistema', 3);
    }
  }, [isGenericError]);

  return (
    <>
      {isGenericError && <DefaultMsgPage traductionKey="generic_error" image="brokenSuitcase" />}
      {!validToken && !isGenericError && !isLoadingAnimation.isLoading && (
        <DefaultMsgPage traductionKey="expired_email_link" onPrimary={handleSendAgain} image="brokenSuitcase" />
      )}

      <SmlsAnimation
        currentState={isLoadingAnimation}
        onExit={onFinishLoading}
        message={{ loading: [t('wait'), t('validating')], success: [t('validate_success')], error: [] }}
        typeModal="full"
        finishMode={loadingFinishMode}
      />
    </>
  );
};

export default Email;
